import { useMemo, useRef } from "react";

import { filterFactory } from "@similarweb/pro-logic-components";
import type { SelectOption } from "@similarweb/pro-logic-components/dist/filter-factory/src/compound/types/select-option";

import { translate } from "app/filters/i18nFilter";
import { MultiselectFilterOption } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/MultiselectFilterOption";
import { ChangeStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/types";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { CHANGE_FILTER_I18N_KEY, DOMAIN_FILTER_I18N_KEY, FILTER_TRACKING_LABEL_KEYS } from "./consts";
import { useSourcesUrlParams } from "./hooks/useSourcesUrlParams";

interface IFilterState {
    domainFilter?: string[];
    changeFilter?: string[];
}

const Filters = filterFactory.createTypedFilters<IFilterState>();

type MultiSelectOption = filterFactory.MultiSelectOption;

interface IURLDataFiltersProps {
    domainFacets: SelectOption[];
    isLoading?: boolean;
    hasPriorWindow?: boolean;
}

const updateUrlParams = ({ domainFilter, changeFilter }: IFilterState): void => {
    void proNavigator.updateParams(
        {
            domainFilter: domainFilter?.length ? domainFilter.join(",") : undefined,
            changeFilter: changeFilter?.length ? changeFilter.join(",") : undefined,
        },
        { reload: false },
    );
};

const createMultiFilterLabel =
    (baseLabel: string, selectedText: string) =>
    (value: string[] | undefined): string => {
        if (!value?.length) {
            return baseLabel;
        }
        if (value.length === 1) {
            return `${baseLabel}: ${value[0]}`;
        }

        return `${baseLabel}: ${value.length} ${selectedText}`;
    };

export const URLDataFilters = ({ domainFacets, isLoading = false, hasPriorWindow }: IURLDataFiltersProps) => {
    const { domainFilter, changeFilter } = useSourcesUrlParams();
    const filterState = useMemo<IFilterState>(
        () => ({
            ...(domainFilter && { domainFilter: domainFilter.split(",") }),
            ...(changeFilter?.length && { changeFilter }),
        }),
        [domainFilter, changeFilter],
    );

    const domainFilterLabel = translate(DOMAIN_FILTER_I18N_KEY);
    const changeFilterLabel = translate(CHANGE_FILTER_I18N_KEY);
    const selectedText = translate("llm.url_analysis.filters.selected");
    const domainPlaceholder = translate("llm.url_analysis.filters.domain.search_placeholder");

    const latestFilterStateRef = useRef<IFilterState>(filterState);

    const handleProviderChange = (newState: IFilterState) => {
        latestFilterStateRef.current = newState;
        updateUrlParams(newState);
    };

    const changeOptions = useMemo<MultiSelectOption[]>(
        () => [
            {
                id: ChangeStatus.New,
                value: ChangeStatus.New,
                label: translate("llm.prompts.filters.change.new"),
            },
            {
                id: ChangeStatus.Lost,
                value: ChangeStatus.Lost,
                label: translate("llm.prompts.filters.change.lost"),
            },
        ],
        [],
    );

    const trackFilter = (eventType: string) => (filterName: string) => {
        if (eventType === "Apply" && filterName === "changeFilter") {
            const values = latestFilterStateRef.current.changeFilter;
            const suffix = values?.length ? `/${values.join(",")}` : "";
            allTrackers.dispatchTrackEvent("LLM Tracker - URL Filter", "Apply", `${changeFilterLabel}${suffix}`);
            return;
        }
        const labelKey = FILTER_TRACKING_LABEL_KEYS[filterName];
        if (labelKey) {
            allTrackers.dispatchTrackEvent("LLM Tracker - URL Filter", eventType, translate(labelKey));
        }
    };

    return (
        <Filters.Provider
            state={filterState}
            onChange={handleProviderChange}
            events={{
                onFilterClick: trackFilter("Click"),
                onFilterChange: trackFilter("Apply"),
            }}
        >
            <Filters.MultiSelect
                name="changeFilter"
                options={changeOptions}
                width="200px"
                isDisabled={isLoading || !hasPriorWindow}
                renderItem={({ label }) => <MultiselectFilterOption label={label} />}
                label={createMultiFilterLabel(changeFilterLabel, selectedText)}
            />
            <Filters.MultiSelect
                name="domainFilter"
                withSearch
                placeholder={domainPlaceholder}
                options={domainFacets}
                width="330px"
                isDisabled={isLoading}
                label={createMultiFilterLabel(domainFilterLabel, selectedText)}
            />
            <Filters.ClearAll />
        </Filters.Provider>
    );
};
